import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "./SupabaseAuthProvider";

const UserDataContext = createContext();

export function UserDataProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // ------------------------------
  // Fetch single user row
  // ------------------------------
  const fetchUserData = async (uid) => {
    if (!uid) return null;

    const { data, error } = await supabase
      .from("users_data")
      .select("*")
      .eq("user_id", uid)
      .maybeSingle();

    if (error) console.error("fetchUserData error:", error);

    return data ?? null;
  };

  // ------------------------------
  // Ensure a user row exists
  // ------------------------------
  const ensureRow = async (uid) => {
    if (!uid) return;

    await supabase.from("users_data").upsert(
      { user_id: uid },
      { onConflict: "user_id", returning: "minimal" }
    );
  };

  // ------------------------------
  // INIT ON LOGIN
  // ------------------------------
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setLoading(true);

      if (!user?.id) {
        setUserData(null);
        setLoading(false);
        return;
      }

      await ensureRow(user.id);

      const data = await fetchUserData(user.id);

      if (!mounted) return;

      setUserData(
        data || {
          user_id: user.id,
          balance: 0,
          total_invested: 0,
          todays_profit: 0,
          monthly_profit: 0,
          total_payout: 0,
          reward_balance: 0,
          user_package: 0,
          pending_profits: [],
          deposit_history: [],
          transactions: [],
        }
      );

      setLoading(false);
    };

    init();
    return () => (mounted = false);
  }, [user?.id]);

  // ------------------------------
  // SAFE update function
  // ------------------------------
  const updateUserDataFields = async (fields = {}) => {
    if (!user?.id) return;

    const updateObj = {
      ...fields,
      updated_at: new Date().toISOString(),
    };

    await supabase
      .from("users_data")
      .update(updateObj)
      .eq("user_id", user.id);

    // Safe merge to prevent overwrites
    setUserData((prev) => ({ ...(prev || {}), ...updateObj }));
  };

  // ------------------------------
  // AUTO-CREDIT ON DEPOSIT SUCCESS
  // ------------------------------
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("userdata-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "users_data",
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newRow = payload.new;
          const oldRow = userData;

          console.log("🔥 Realtime Update", newRow);

          // Check if a transaction switched to SUCCESS
          if (newRow?.transactions && oldRow?.transactions) {
            const oldTx = oldRow.transactions;
            const newTx = newRow.transactions;

            const newlySuccess = newTx.find(
              (tx) =>
                tx.status === "success" &&
                !oldTx.some(
                  (o) => o.id === tx.id && o.status === "success"
                )
            );

            if (newlySuccess) {
              const amount = Number(newlySuccess.amount || 0);

              console.log("💰 Auto-credit triggered:", amount);

              const newBalance = (newRow.balance || 0) + amount;

              await updateUserDataFields({ balance: newBalance });
            }
          }

          // Update UI
          setUserData(newRow);
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user?.id, userData]);

  // ------------------------------
  // Add Balance
  // ------------------------------
  const addBalance = async (amt) => {
    if (!user?.id) return;
    const n = Number(amt || 0);

    const newBal = (userData?.balance || 0) + n;

    await updateUserDataFields({ balance: newBal });
  };

  // ------------------------------
  // Subtract Balance
  // ------------------------------
  const subtractBalance = async (amt) => {
    if (!user?.id) return;
    const n = Number(amt || 0);

    const newBal = Math.max(0, (userData?.balance || 0) - n);

    await updateUserDataFields({ balance: newBal });
  };

  // ------------------------------
  // EXPORT API
  // ------------------------------
  const api = {
    loading,
    userData,
    refresh: async () => {
      if (!user?.id) return;
      const fresh = await fetchUserData(user.id);
      setUserData(fresh);
    },
    updateUserDataFields,
    addBalance,
    subtractBalance,
  };

  return (
    <UserDataContext.Provider value={api}>
      {children}
    </UserDataContext.Provider>
  );
}

export function useUserData() {
  return useContext(UserDataContext);
}
